/** Ce que chaque écran reçoit pour se dessiner. */
import type { Etat, Personnage } from "../sim/etat.ts"
import type { Contenu } from "../sim/contenu.ts"
import type { StatsPerso } from "../sim/combat.ts"

import type { Issue } from "../sim/forge.ts"
import type { Chiffres } from "../net/carte.ts"

export interface Ctx {
  etat: Etat
  contenu: Contenu
  /** le personnage actif ; absent tant qu'aucun n'existe */
  p: Personnage | undefined
  /** statistiques du personnage actif, équipement et Dofus compris */
  s: StatsPerso | null
  /** sous-onglet courant de l'écran affiché */
  sous: string
  /** onglet interne au détail d'une zone */
  sousZone: string
  /** tri courant du sac */
  tri: "nom" | "niveau" | "unitaire" | "lot" | "quantite" | "perfection"
  /** catégorie de ressources affichée */
  filtre: string
  /** n'afficher que les recettes réalisables */
  /** filtre de l'atelier : "" (toutes), "ok" (réalisables), "neuf" (jamais fabriquées) */
  filtreCraft: string
  /** objet détaillé dans le sac */
  selection: number | null
  /** texte cherché (atelier ou monde) */
  recherche: string
  /** exemplaire d'équipement sélectionné, porté ou au coffre */
  pieceChoisie: string | null
  /** Dofus détaillé sous la poupée */
  dofusChoisi: number | null
  /** exemplaire ouvert à la forge, porté ou au coffre */
  forgeUid: string | null
  /** ce que le dernier lot de forgemagie a produit */
  forgeIssues: Issue[]
  /** l'Hôtel des Ventes : ce qui est chargé, et ce qu'on est en train de vendre */
  marche: import("./vues/marche.ts").EtatMarche
  /** volet ouvert dans l'Hôtel des Ventes */
  sousMarche: string
  /** catégorie affichée sur l'étal */
  filtreEtal: string
  /** tri de l'étal */
  triEtal: string
  /** tranche de niveau affichée sur l'étal */
  nivEtal: [number, number]
  /** annonce dépliée sur ses statistiques */
  annonceOuverte: number | null
  /** table de butin dépliée : « f:12 » pour une famille, « d:34 » pour un donjon */
  butinOuvert: string | null
  /** filtre de la liste des donjons : "" (tous), "cle" (clé en main), "reste" (jamais vaincus) */
  filtreDonjon: string
  /** ressource dont la liste « sert à » est dépliée */
  sertAOuvert: number | null
  /** volet ouvert sous l'onglet Chat : salon, nouveautés, retours */
  sousSocial: string
  /** nouveautés et retours des joueurs (GDD §27) */
  social: import("./vues/nouvelles.ts").EtatSocial
  /** partage du profil (GDD §28) */
  partage: { envoi: boolean; message: string | null; erreur: boolean }
  /**
   * Les chiffres **du miroir** (§18) : ceux que montrent la page publique et la
   * carte. `null` tant qu'ils ne sont pas revenus, ou hors ligne.
   */
  chiffres: Chiffres | null
  /** pièces du coffre cochées pour une vente ou un broyage en lot */
  lotPieces: Set<string>
  /** ressources cochées pour une vente en lot */
  lotRessources: Set<number>
  /** le mode sélection est-il actif ? */
  enLot: boolean
  /** combien vendre de la ressource ouverte */
  qteVente: number
  /** combien de points placer d'un coup dans une caractéristique */
  pas: string
  /** emplacement demandé dans l'atelier, "" pour tous */
  slotCraft: string
  /** caractéristiques exigées du résultat, cumulatives */
  caracCraft: string[]
  /** tranche de niveau demandée dans l'atelier */
  nivCraft: [number, number]
  /** panneau d'affinage de l'atelier déplié */
  affiner: boolean
  /** objet dont la liste complète des sources est dépliée */
  sourcesOuvertes: number | null
  /** panoplie dépliée sous une fiche d'objet */
  panoOuverte: number | null
  /** échelle complète des paliers dépliée, pour cette panoplie */
  paliersOuverts: number | null
  /** texte cherché dans les succès */
  rechercheSucces: string
  /** quels succès afficher : tous, ceux à faire, ceux obtenus */
  filtreSucces: "tout" | "restants" | "faits"
  /** catégorie de succès dépliée, une seule à la fois */
  categorieSucces: string | null
  /** créature de collection dont la fiche est ouverte */
  monstreChoisi: number | null
  /** recette dont le détail est déplié dans l'atelier, une seule à la fois */
  recetteOuverte: number | null
  /** ce que le classement a chargé, tenu hors de l'état de jeu */
  classement: import("./vues/classement.ts").EtatClassement
}

/**
 * Barre de sous-onglets, motif récurrent des écrans du jeu.
 * `attr` permet d'avoir deux niveaux imbriqués : les zones, puis leur contenu.
 */
export function sousOnglets(actif: string, items: [cle: string, libelle: string][], attr = "sous") {
  return `<nav class="sousonglets">${items.map(([cle, lib]) =>
    `<button data-${attr}="${cle}" aria-current="${cle === actif}">${lib}</button>`).join("")}</nav>`
}
